import {
  RMTLocationCategory,
  RMTLocationSourceType,
  RMTMeans,
  RMTMode,
  RMTRouteType,
} from '@/app/factory-database/src/enums/rmt';
import { ActivityUnit } from '@/app/org-cfv/src/enums/ActivityDataTypeEnum';
import {
  EmissionFactorByFactorErrorType,
  EmissionSourceFactorType,
  EmissionSourceFactorUnitType,
} from '@/app/org-cfv/src/enums/EmissionSourceEnum';
import { PaginationMeta, ResponseSuccess } from '@/typings/api';
import { DocumentType } from '@/typings/common';

export interface EmployeeCommutingList {
  id: string;
  employeeIdentifier: string;
  employeeName: string;
  name: string;
  means: RMTMeans[];
  totalDistance: number;
  distanceUnit: ActivityUnit;
  factorVersion: string;
  isActivated: boolean;
  isReferenced: boolean;
}

export enum RouteTripType {
  RoundTrip = 'ROUND_TRIP',
  OnewayTrip = 'ONEWAY_TRIP',
}

export interface EmployeeCommutingLocation {
  region: string;
  postalCode?: string;
  address: string;
}

export interface EmployeeCommutingLocationDetail {
  source: RMTLocationSourceType;
  category: RMTLocationCategory;
  region: string;
  postalCode?: string;
  address: string;
  name?: string;
  key?: string;
  no?: string;
}

export interface EmployeeCommutingSegment {
  id: string;
  mode: RMTMode;
  locationStartCategory: RMTLocationCategory;
  locationEndCategory: RMTLocationCategory;
  no: string;
}

export interface EmployeeCommutingSegmentDetail {
  id: string;
  mode: RMTMode;
  means: RMTMeans;
  distance: number;
  distanceUnit: ActivityUnit;
  locationStart: EmployeeCommutingLocationDetail;
  locationEnd: EmployeeCommutingLocationDetail;
  factor: EmployeeCommutingSegmentFactor;
  documentFileId: string;
}

export interface EmployeeCommutingFactor {
  id: string;
  version: string;
  activityUnitType: EmissionSourceFactorUnitType;
  activityUnit: ActivityUnit;
  ghgUnit: ActivityUnit;
  factor: number;
}

export interface EmployeeCommutingSegmentFactor {
  factorType: EmissionSourceFactorType;
  factorSourceKey: string;
  factorSource: string;
  factorNameKey: string;
  factorName: string;
  factorBeginAt: string;
  factorErrorType: EmissionFactorByFactorErrorType;
  factor: number;
  ghgUnit: ActivityUnit;
  activityUnitType: EmissionSourceFactorUnitType;
  activityUnit: ActivityUnit;
  documents: { id: string; type: DocumentType }[];
}

export interface EmployeeCommuting {
  id: string;
  employeeIdentifier: string;
  employeeName: string;
  name: string;
  employeeLocation: EmployeeCommutingLocation;
  destinationLocation: EmployeeCommutingLocation;
  transportationRoute: RMTRouteType;
  transportationRouteTripType: RouteTripType;
  segments: EmployeeCommutingSegment[];
  factor: EmployeeCommutingFactor;
}

export type EmployeeCommutingDetail = Omit<EmployeeCommuting, 'segments'> & {
  segments: EmployeeCommutingSegmentDetail[];
};

export enum GIN_BOUNDARY_DATABASE_EMPLOYEE_COMMUTING_API_KEY {
  POST = 'POST/GIN_BOUNDARY_DATABASE_EMPLOYEE_COMMUTING',
  GET = 'GET/GIN_BOUNDARY_DATABASE_EMPLOYEE_COMMUTING',
  PATCH = 'PATCH/GIN_BOUNDARY_DATABASE_EMPLOYEE_COMMUTING',
  DELETE = 'DELETE/GIN_BOUNDARY_DATABASE_EMPLOYEE_COMMUTING',
  // [PLOP ANCHOR] apisKeyEnum
}

export type EmployeeCommutingPostBody = Omit<EmployeeCommutingDetail, 'id'> & {
  factor: Omit<EmployeeCommutingFactor, 'id'>;
  segments: Omit<EmployeeCommutingSegmentDetail, 'id'>[];
};

export interface PostGinBoundaryDatabaseEmployeeCommutingParams {
  query: { boundaryId: string };
  body: EmployeeCommutingPostBody;
}
export interface PostGinBoundaryDatabaseEmployeeCommutingResData {
  id: string;
}
export type PostGinBoundaryDatabaseEmployeeCommutingRes =
  ResponseSuccess<PostGinBoundaryDatabaseEmployeeCommutingResData>;

export interface GetGinBoundaryDatabaseEmployeeCommutingParams {
  query: { boundaryId: string; page: number; limit: number; fieldSet?: Record<string, string> };
}
export interface GetGinBoundaryDatabaseEmployeeCommutingResData {
  list: EmployeeCommutingList[];
  meta: PaginationMeta;
}
export type GetGinBoundaryDatabaseEmployeeCommutingRes =
  ResponseSuccess<GetGinBoundaryDatabaseEmployeeCommutingResData>;

export interface PatchGinBoundaryDatabaseEmployeeCommutingParams {
  query: { boundaryId: string; employeeCommutingId: string };
  body: EmployeeCommutingPostBody;
}
export interface PatchGinBoundaryDatabaseEmployeeCommutingResData {
  id: string;
}
export type PatchGinBoundaryDatabaseEmployeeCommutingRes =
  ResponseSuccess<PatchGinBoundaryDatabaseEmployeeCommutingResData>;

export interface DeleteGinBoundaryDatabaseEmployeeCommutingParams {
  query: { boundaryId: string; employeeCommutingId: string };
}
export interface DeleteGinBoundaryDatabaseEmployeeCommutingResData {
  id: string;
}
export type DeleteGinBoundaryDatabaseEmployeeCommutingRes =
  ResponseSuccess<DeleteGinBoundaryDatabaseEmployeeCommutingResData>;

// [PLOP ANCHOR] dtoDefinition
